import dynamic from "next/dynamic";

import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { faCalendarDays } from "@fortawesome/free-regular-svg-icons";
import { Box, IconFa } from "@cruk/cruk-react-components";

import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { fwsUrlPageParentMemberships } from "@fwa/src/services/fundraisingPageService";

import { PageMemberOf } from "@fwa/src/components/PageMemberOf";
import { ErrorBoundary } from "@fwa/src/components/ErrorBoundary";

import sharedStyles from "@fwa/src/components/styles.module.css";
import styles from "@fwa/src/components/FundraisingPageActivityBlock/styles.module.css";

import {
  type FundraisingPageType,
  type FwsResponseData,
  type TeamMemberType,
} from "@fwa/src/types";

const FundraisingPageActivityDateForm = dynamic(
  () => import("@fwa/src/components/FundraisingPageActivityDateForm"),
  { ssr: false },
);
const FundraisingPageActivityNameForm = dynamic(
  () => import("@fwa/src/components/FundraisingPageActivityNameForm"),
  { ssr: false },
);
// because this renders local date strings we need to make sure this only loads client side
// to avoid hydration issues with server side rendering
const FundraisingPageActivityDateBlock = dynamic(
  () => import("@fwa/src/components/FundraisingPageActivityDateBlock"),
  { ssr: false },
);

export const FundraisingPageActivityBlock = ({
  page,
  canEdit,
  updateAndMutatePage,
}: {
  page: FundraisingPageType;
  canEdit: boolean;
  updateAndMutatePage: (
    data: Partial<FundraisingPageType>,
    refresh?: boolean,
  ) => Promise<void | FundraisingPageType>;
}) => {
  const url = page
    ? `${fwsUrlPageParentMemberships({
        pageId: page.uniqueId,
        currentPage: 1,
        itemsPerPage: 3,
      })}`
    : null;
  const { data } = useSwrGet<FwsResponseData<TeamMemberType>>(url);
  const memberships = data?.results || [];
  const activityString = page?.activityName || page?.event?.eventName || "";

  if (canEdit) {
    return (
      <Box
        backgroundColor="background-light"
        padding="s"
        paddingTop="none"
        data-component="page-activity-block"
      >
        <div className={styles["activity-items-wrapper"]}>
          <div
            className={sharedStyles["activity-item-wrapper"]}
            data-component="page-activity-name-form"
          >
            <FundraisingPageActivityNameForm
              activityName={activityString}
              handleEditData={updateAndMutatePage}
            />
          </div>
          <div
            className={sharedStyles["activity-item-wrapper"]}
            data-component="page-activity-date-form"
          >
            <ErrorBoundary>
              <FundraisingPageActivityDateForm
                startDate={page.activityDateTime || null}
                endDate={page.activityDateTimeEnd || null}
                handleEditData={updateAndMutatePage}
              />
            </ErrorBoundary>
          </div>
          {data ? (
            <ErrorBoundary>
              <PageMemberOf page={page} fallbackData={data} canEdit={canEdit} />
            </ErrorBoundary>
          ) : null}
        </div>
      </Box>
    );
  }

  if (activityString.length || page.activityDateTime || !!memberships.length) {
    return (
      <Box
        backgroundColor="background-light"
        padding="s"
        paddingTop="none"
        data-component="page-activity-block"
      >
        <div className={styles["activity-items-wrapper"]}>
          {activityString.length ? (
            <div
              className={sharedStyles["activity-item-wrapper"]}
              data-component="page-activity-name"
            >
              <div className={sharedStyles["activity-item"]}>
                <div className={sharedStyles["activity-icon-wrapper"]}>
                  <IconFa faIcon={faBullhorn} size="100%" />
                </div>
                <p className={styles["text-responsive"]}>{activityString}</p>
              </div>
            </div>
          ) : null}
          {page.activityDateTime ? (
            <div
              className={sharedStyles["activity-item-wrapper"]}
              data-component="page-activity-date"
            >
              <div className={sharedStyles["activity-item"]}>
                <div className={sharedStyles["activity-icon-wrapper"]}>
                  <IconFa faIcon={faCalendarDays} size="100%" />
                </div>
                <ErrorBoundary>
                  <FundraisingPageActivityDateBlock page={page} />
                </ErrorBoundary>
              </div>
            </div>
          ) : null}

          {data ? (
            <PageMemberOf page={page} fallbackData={data} canEdit={canEdit} />
          ) : null}
        </div>
      </Box>
    );
  }

  return null;
};

export default FundraisingPageActivityBlock;
